import EventBus from 'eventing-bus';
import { useHistory } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

import constants from '../../constants';
import PermissionError from '../PermissionError';

const GlobalErrorHandler = () => {
  const [permissionError, setPermissionError] = useState(null);
  const [showPermissionError, setShowPermissionError] = useState(false);
  const history = useHistory();
  
  useEffect(() => {
    // Subscribe to permission error events
    const handlePermissionError = (error) => {
      console.log('Permission error received (403):', error);
      
      // For 403 errors, always show a simple error modal
      // Don't redirect to access denied page
      setPermissionError(error);
      setShowPermissionError(true);
    };
    
    // Subscribe to API errors that might be permission-related
    const handleApiError = (error) => {
      // Check if it's a 403 error (Forbidden)
      if (error && (error.status === 403 || error.code === 403)) {
        console.log('403 error detected:', error);
        handlePermissionError(error.message || error.data || 'You don\'t have permission to perform this action.');
      }
    };
    
    // Handle workspace access errors differently
    const handleWorkspaceError = (error) => {
      console.log('Workspace error received:', error);
      // Store error details in sessionStorage for the access denied page to retrieve
      sessionStorage.setItem('accessDeniedError', JSON.stringify({
        title: 'Workspace Access Denied',
        message: error || 'You don\'t have access to this workspace.',
        suggestions: [
          'Verify you are in the correct workspace',
          'Request access from the workspace owner',
          'Contact support if you believe this is an error'
        ]
      }));
      // Redirect to AccessDenied page
      window.location.href = '/access-denied';
    };
    
    // Handle potential permission-related 404 errors
    const handlePotentialPermission404 = (errorData) => {
      console.log('handlePotentialPermission404 called with:', errorData);
      
      // Extract object type from URL for better messaging
      const url = errorData.url || '';
      const objectTypeMatch = url.match(/\/api\/(?:objects\/(\w+)\/|(\w+)\/)/);
      const objectType = objectTypeMatch ? (objectTypeMatch[1] || objectTypeMatch[2]) : 'resource';
      
      // Convert to singular and capitalize
      const object = constants.getObjectSchema(objectType);
      console.log('object = ', object);

      if(object?.['label']) {
        // Store error details in sessionStorage for the access denied page to retrieve
        sessionStorage.setItem('accessDeniedError', JSON.stringify({
          title: `${object['label']} Not Found`,
          message: `This ${object['name']} does not exist.`,
          suggestions: [
            `Verify the ${object['name']} ID is correct`,
            'Check if the item was deleted',
            'Verify you are in the correct workspace',
            'Go back to the previous page'
          ]
        }));
        // Redirect to access denied page
        window.location.href = '/access-denied';
      } else {
        // Store error details in sessionStorage for the access denied page to retrieve
        sessionStorage.setItem('accessDeniedError', JSON.stringify({
          title: `Resource Not Found`,
          message: `This resource does not exist.`,
          suggestions: [
            `Verify the resource ID is correct`,
            'Check if the item was deleted',
            'Verify you are in the correct workspace',
            'Go back to the previous page'
          ]
        }));
        // Redirect to access denied page
        window.location.href = '/access-denied';
      }
    };
    
    // Subscribe to events
    const unsubscribePermissionError = EventBus.on('permission:error', handlePermissionError);
    const unsubscribeApiError = EventBus.on('api:error', handleApiError);
    const unsubscribeError403 = EventBus.on('error:403', handlePermissionError);
    const unsubscribeWorkspaceError = EventBus.on('workspace:access-denied', handleWorkspaceError);
    const unsubscribePotentialPermission404 = EventBus.on('api:potential-permission-404', handlePotentialPermission404);

    // Cleanup
    return () => {
      unsubscribePermissionError();
      unsubscribeApiError();
      unsubscribeError403();
      unsubscribeWorkspaceError();
      unsubscribePotentialPermission404();
    };
  }, [history]);
  
  const handleClose = () => {
    // Check if this is a user account status error (paused/deactivated)
    const isUserAccountError = permissionError && ( permissionError.includes('account has been paused') || permissionError.includes('account has been deactivated') );
    setShowPermissionError(false);
    setPermissionError(null);

    // If user account is paused/deactivated, logout after closing modal
    if (isUserAccountError) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };
  
  const handleRequestAccess = () => {
    // This could open an email client or a form to request access
    const subject = encodeURIComponent('Request for Additional Permissions');
    const body = encodeURIComponent('Hello,\n\nI would like to request additional permissions to perform the following action:\n\n[Please describe what you were trying to do]\n\nThank you.');
    window.location.href = `mailto:admin@example.com?subject=${subject}&body=${body}`;
    handleClose();
  };
  
  return (
    <PermissionError 
      isOpen={showPermissionError}
      toggle={handleClose}
      error={permissionError}
      // onRequestAccess={handleRequestAccess}
    />
  );
};

export default GlobalErrorHandler;